// ts-rest contract for the ontology-driven reconciliation surface.
//
// Endpoints under /api/reconciliation/* dispatch through the wealth
// ontology registry (`ResolveBreak`, `AssignBreak`, `AutoResolveBreaks`).
// The legacy MCP-level `resolve_break` tool in
// `apps/server/src/lib/mcp/tools-ops.ts` remains in place during the
// gradual migration; new callers should prefer these endpoints.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

export const reconciliationBreakStatusSchema = z.enum([
    'open',
    'auto_resolved',
    'manually_resolved',
]);
export type ReconciliationBreakStatus = z.infer<typeof reconciliationBreakStatusSchema>;

// Reconciliation type extracted from a break's `breakReason` prefix
// (e.g. "rbc:quantity_mismatch" → "rbc"). The recon engine encodes the
// type into the reason so a single endpoint can return every type and
// the UI can filter client-side.
export const reconciliationKindSchema = z.enum(['holdingPosition', 'cashPosition']);
export type ReconciliationKind = z.infer<typeof reconciliationKindSchema>;

// Diff payload joined from the linked ReconciliationResult.diffFields.
// Loose record-of-unknown so the engine can extend without a contract churn;
// the page reads specific keys defensively.
export const reconciliationBreakDiffFieldsSchema = z.record(z.string(), z.unknown()).nullable();

export const reconciliationBreakSchema = z.object({
    id: z.string(),
    status: reconciliationBreakStatusSchema,
    breakReason: z.string(),
    age: z.number().int(),
    latestReconciliationResultId: z.string(),
    assigneeId: z.string().nullable(),
    assignedAt: z.string().nullable().optional(),
    assignmentReason: z.string().nullable().optional(),
    resolvedAt: z.string().nullable().optional(),
    resolutionNote: z.string().nullable().optional(),
    sourceUploadIds: z.array(z.string()),
    sourceRecordIds: z.array(z.string()),
    /**
     * `rbc` / `lloyds` / `fund` — extracted from `breakReason` prefix.
     * Returned on the wire so clients don't have to re-parse the string.
     */
    reconciliationType: z.string().nullable().optional(),
    /** `holdingPosition` or `cashPosition`, from the linked result. */
    objectType: reconciliationKindSchema.nullable().optional(),
    /** Numerical difference from the linked result, mirrored here. */
    differenceValue: z.number().nullable().optional(),
    /** Match status from the linked result (`break`, `unmatched`, ...). */
    matchStatus: z.string().nullable().optional(),
    /** Joined ReconciliationResult.diffFields payload — rich display data. */
    diffFields: reconciliationBreakDiffFieldsSchema.optional(),
});
export type ReconciliationBreakDto = z.infer<typeof reconciliationBreakSchema>;

export const resolveBreakRequestSchema = z.object({
    resolutionNote: z.string().min(1),
});
export type ResolveBreakRequest = z.infer<typeof resolveBreakRequestSchema>;

export const assignBreakRequestSchema = z.object({
    assigneeId: z.string().uuid(),
    assignmentReason: z.string().min(1).optional(),
});
export type AssignBreakRequest = z.infer<typeof assignBreakRequestSchema>;

export const autoResolveBreaksRequestSchema = z.object({
    reconciliationType: z.string().optional(),
});
export type AutoResolveBreaksRequest = z.infer<typeof autoResolveBreaksRequestSchema>;

export const autoResolveBreaksResponseSchema = z.object({
    closed: z.array(reconciliationBreakSchema),
});
export type AutoResolveBreaksResponse = z.infer<typeof autoResolveBreaksResponseSchema>;

// --- Issue #34: upload + run endpoints (Cape Wrath holdings) ---

export const reconciliationFileTypeSchema = z.enum([
    'tercero_f1',
    'tercero_f3',
    'tercero_f7',
    'tercero_f1_f7_cash',
    'caceis_holdings',
    'caceis_cash',
    'butterfield_holdings',
    'butterfield_cash',
    // Issue #41 — Lloyds reconciliation inputs.
    'lloyds_portal',
    'lloyds_portfolio_holdings',
    'lloyds_mapping',
    // Issue #40 — RBC reconciliation inputs.
    'rbc_portfolio_holdings',
    'rbc_valuation',
    'rbc_mapping',
]);
export type ReconciliationFileType = z.infer<typeof reconciliationFileTypeSchema>;

export const uploadReconciliationFileResponseSchema = z.object({
    ok: z.literal(true),
    fileType: reconciliationFileTypeSchema,
    scope: z.string(),
    snapshotId: z.string(),
    versionN: z.number().int(),
    rowCount: z.number().int(),
});
export type UploadReconciliationFileResponse = z.infer<
    typeof uploadReconciliationFileResponseSchema
>;

export const runFundReconciliationRequestSchema = z.object({
    fund: z.enum(['cape_wrath', 'small_cap', 'onward_opportunities']),
    scope: z.string().min(1),
});
export type RunFundReconciliationRequest = z.infer<typeof runFundReconciliationRequestSchema>;

export const runFundReconciliationResponseSchema = z.object({
    ok: z.literal(true),
    resultsCount: z.number().int(),
    breaksOpened: z.number().int(),
});
export type RunFundReconciliationResponse = z.infer<typeof runFundReconciliationResponseSchema>;

// --- Issue #41: Lloyds reconciliation run ---
export const runLloydsReconciliationRequestSchema = z.object({
    scope: z.string().min(1),
});
export type RunLloydsReconciliationRequest = z.infer<typeof runLloydsReconciliationRequestSchema>;
export const runLloydsReconciliationResponseSchema = z.object({
    ok: z.literal(true),
    resultsCount: z.number().int(),
    breaksOpened: z.number().int(),
});
export type RunLloydsReconciliationResponse = z.infer<typeof runLloydsReconciliationResponseSchema>;

// --- Issue #40: RBC reconciliation run ---
export const runRbcReconciliationRequestSchema = z.object({
    scope: z.string().min(1),
});
export type RunRbcReconciliationRequest = z.infer<typeof runRbcReconciliationRequestSchema>;
export const runRbcReconciliationResponseSchema = z.object({
    ok: z.literal(true),
    resultsCount: z.number().int(),
    breaksOpened: z.number().int(),
});
export type RunRbcReconciliationResponse = z.infer<typeof runRbcReconciliationResponseSchema>;

export const breakResponseSchema = z.object({
    ok: z.literal(true),
    break: reconciliationBreakSchema,
});
export type BreakResponse = z.infer<typeof breakResponseSchema>;

export const listBreaksResponseSchema = z.object({
    breaks: z.array(reconciliationBreakSchema),
});
export type ListBreaksResponse = z.infer<typeof listBreaksResponseSchema>;

// ---------------------------------------------------------------------------
// Latest-run result set (matched + within-tolerance + breaks)
// ---------------------------------------------------------------------------

// One `ReconciliationResult` from the latest run of each reconciliation type,
// with the break-lifecycle fields joined in when a `ReconciliationBreak` points
// at the result (break/unmatched rows). matched / within-tolerance rows carry
// null break-join fields. Results accumulate across runs, so this is always
// scoped to each type's latest completed run server-side.
// MASON's approved classification of a break, projected from the break's
// latest enrichment finding (populated only once a
// ReconciliationClassificationFinding has been approved — approval patches the
// break and sets latestEnrichmentFindingId). Surfaced in the break's
// expanded-row drawer; null until a finding is approved.
export const masonClassificationSchema = z.object({
    findingId: z.string(),
    breakType: z.string(),
    isChronic: z.boolean(),
    classificationBasis: z.string(),
    chronicBasis: z.string().nullable(),
    recommendedAction: z.string().nullable(),
    sideBySideEvidence: z
        .object({
            custodianLine: z.string(),
            internalLine: z.string(),
            diffSummary: z.string(),
        })
        .nullable(),
    evaluation: z
        .array(
            z.object({
                condition: z.string(),
                observed: z.string(),
                threshold: z.string(),
                pass: z.boolean(),
                note: z.string().nullable(),
            }),
        )
        .nullable(),
});
export type MasonClassificationDto = z.infer<typeof masonClassificationSchema>;

export const reconciliationResultSchema = z.object({
    resultId: z.string(),
    matchStatus: z.enum(['matched', 'withinTolerance', 'break', 'unmatched']),
    reconciliationType: z.string(),
    objectType: reconciliationKindSchema,
    differenceValue: z.number(),
    diffFields: reconciliationBreakDiffFieldsSchema,
    // Break-join fields — populated only when a break references this result.
    breakId: z.string().nullable(),
    breakStatus: reconciliationBreakStatusSchema.nullable(),
    breakReason: z.string().nullable(),
    age: z.number().int().nullable(),
    assigneeId: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    /** MASON's approved classification, once an enrichment finding is approved. */
    masonClassification: masonClassificationSchema.nullable().optional(),
    /**
     * True when a MASON `ReconciliationClassificationFinding` is still pending
     * review against this break. Drives the row's "MASON review" badge and the
     * pending-review card rendered in the expanded row. At most one pending
     * finding exists per break (supersession keeps it to one).
     */
    hasPendingMasonReview: z.boolean().optional(),
});
export type ReconciliationResultDto = z.infer<typeof reconciliationResultSchema>;

export const listResultsResponseSchema = z.object({
    results: z.array(reconciliationResultSchema),
});
export type ListResultsResponse = z.infer<typeof listResultsResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const reconciliationContract = c.router(
    {
        listBreaks: {
            method: 'GET',
            path: '/reconciliation/breaks',
            query: z
                .object({
                    status: reconciliationBreakStatusSchema.optional(),
                    // Pagination (audit finding P2). The breaks table grows
                    // unboundedly across reconciliation runs, so the read is
                    // bounded server-side. Both are optional with a generous
                    // server default, so existing callers (which pass neither)
                    // see no change in behaviour.
                    limit: z.coerce.number().int().positive().max(1000).optional(),
                    offset: z.coerce.number().int().nonnegative().optional(),
                })
                .optional(),
            responses: {
                200: listBreaksResponseSchema,
            },
            summary:
                'List reconciliation breaks (optionally filtered by status; paginated via limit/offset)',
        },
        listResults: {
            method: 'GET',
            path: '/reconciliation/results',
            query: z
                .object({
                    // Scope to one reconciliation type (audit finding P2): the
                    // results table is append-only and accumulates every run's
                    // output for all three types. When a client only renders
                    // one type, this pushes the type filter to SQL instead of
                    // reading all three types' result sets. Optional — omitting
                    // it returns the latest run of every type as before.
                    reconciliationType: z.enum(['rbc', 'lloyds', 'fund']).optional(),
                })
                .optional(),
            responses: {
                200: listResultsResponseSchema,
            },
            summary:
                "List the latest run's full result set per reconciliation type (matched + within-tolerance + breaks); client filters by category. Optionally scoped to a single reconciliationType.",
        },
        resolveBreak: {
            method: 'POST',
            path: '/reconciliation/breaks/:id/resolve',
            body: resolveBreakRequestSchema,
            responses: {
                200: breakResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Manually resolve a reconciliation break (registry-dispatched)',
        },
        assignBreak: {
            method: 'POST',
            path: '/reconciliation/breaks/:id/assign',
            body: assignBreakRequestSchema,
            responses: {
                200: breakResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Assign a reconciliation break to an investigator (registry-dispatched)',
        },
        autoResolveBreaks: {
            method: 'POST',
            path: '/reconciliation/auto-resolve-breaks',
            body: autoResolveBreaksRequestSchema,
            responses: {
                200: autoResolveBreaksResponseSchema,
            },
            summary: 'Run the AutoResolveBreaks derive Function (registry-dispatched)',
        },
        runFundReconciliation: {
            method: 'POST',
            path: '/reconciliation/runs',
            body: runFundReconciliationRequestSchema,
            responses: {
                200: runFundReconciliationResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Invoke the reconcile-fund Function for a configured fund + scope.',
        },
        runLloydsReconciliation: {
            method: 'POST',
            path: '/reconciliation/lloyds-runs',
            body: runLloydsReconciliationRequestSchema,
            responses: {
                200: runLloydsReconciliationResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Invoke the reconcile-lloyds Function for the given scope.',
        },
        runRbcReconciliation: {
            method: 'POST',
            path: '/reconciliation/rbc-runs',
            body: runRbcReconciliationRequestSchema,
            responses: {
                200: runRbcReconciliationResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Invoke the reconcile-rbc Function for the given scope.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
